import { registerSW } from "virtual:pwa-register";
import { toast } from "./ui/toast";

/** Как часто спрашивать сервер о новой версии, пока приложение открыто. */
const CHECK_EVERY_MS = 15 * 60_000;

/** Можно перезагрузить незаметно: человек ушёл со страницы и не оставил недописанного сообщения. */
const safeToReload = () =>
  document.visibilityState === "hidden" && !(document.querySelector<HTMLTextAreaElement>("#composer")?.value.trim());

/**
 * Сервис-воркер кеширует оболочку приложения. Новая версия ставится сама: как только человек
 * переключился на другое окно или свернул приложение, страница тихо перезагружается. Без этого
 * старая версия жила до закрытия всех вкладок, и выкатки не доходили до тех, кто держит вкладку открытой.
 */
let registration: ServiceWorkerRegistration | undefined;
let applyNow: (() => void) | undefined;
let waiting = false;

/** «О программе»: можно ли проверить обновление (сервис-воркер работает). */
export const canCheckUpdate = () => !!registration;

/** Спросить сервер о новой версии; true — новая версия скачана и ждёт перезагрузки. */
export async function checkForUpdate(): Promise<boolean> {
  if (!registration) return false;
  if (waiting) return true;
  await registration.update().catch(() => {});
  // Новая версия ставится в фоне: ждём, пока сервис-воркер её примет.
  for (let i = 0; i < 20 && !waiting; i++) {
    if (!registration.installing && !registration.waiting && i > 2) break;
    await new Promise((r) => setTimeout(r, 250));
  }
  return waiting;
}

/** Подключить скачанную новую версию сейчас (страница перезагрузится). */
export const applyUpdate = () => applyNow?.();

export function registerPwa() {
  if (!("serviceWorker" in navigator) || import.meta.env.DEV) return;
  const updateSW = registerSW({
    onNeedRefresh() {
      if (waiting) return;
      waiting = true;
      applyNow = () => void updateSW(true);
      toast("Вышла новая версия Ассистента: она подключится, когда вы переключитесь на другое окно");
      const apply = () => {
        if (!safeToReload()) return;
        document.removeEventListener("visibilitychange", apply);
        void updateSW(true);
      };
      document.addEventListener("visibilitychange", apply);
    },
    onRegisteredSW(_url, r) {
      if (!r) return;
      registration = r;
      const check = () => {
        if (navigator.onLine) void r.update().catch(() => {});
      };
      setInterval(check, CHECK_EVERY_MS);
      // Вернулись к вкладке или приложению после перерыва — проверим сразу.
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });
    },
  });
}
